// Currency utilities for Klorah React application

export const SUPPORTED_CURRENCIES = [
  'USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF', 'CNY', 'HKD', 'NZD',
  'BMD',  // Bermudian Dollar - newly added for Bermuda support
  'SGD', 'NOK', 'SEK', 'DKK', 'PLN', 'CZK', 'HUF', 'INR', 'KRW', 'MYR',
  'THB', 'BRL', 'MXN', 'ZAR', 'TRY', 'RUB', 'AED'
];

export const CURRENCY_INFO = {
  'USD': { symbol: '$', name: 'US Dollar', decimals: 2 },
  'EUR': { symbol: '€', name: 'Euro', decimals: 2 },
  'GBP': { symbol: '£', name: 'British Pound', decimals: 2 },
  'CAD': { symbol: 'C$', name: 'Canadian Dollar', decimals: 2 },
  'AUD': { symbol: 'A$', name: 'Australian Dollar', decimals: 2 },
  'JPY': { symbol: '¥', name: 'Japanese Yen', decimals: 0 },
  'CHF': { symbol: 'CHF', name: 'Swiss Franc', decimals: 2 },
  'CNY': { symbol: '¥', name: 'Chinese Yuan', decimals: 2 },
  'HKD': { symbol: 'HK$', name: 'Hong Kong Dollar', decimals: 2 },
  'NZD': { symbol: 'NZ$', name: 'New Zealand Dollar', decimals: 2 },
  'BMD': { symbol: 'BD$', name: 'Bermudian Dollar', decimals: 2 },  // Bermudian Dollar support
  'SGD': { symbol: 'S$', name: 'Singapore Dollar', decimals: 2 },
  'NOK': { symbol: 'kr', name: 'Norwegian Krone', decimals: 2 },
  'SEK': { symbol: 'kr', name: 'Swedish Krona', decimals: 2 },
  'DKK': { symbol: 'kr', name: 'Danish Krone', decimals: 2 },
  'PLN': { symbol: 'zł', name: 'Polish Zloty', decimals: 2 },
  'CZK': { symbol: 'Kč', name: 'Czech Koruna', decimals: 2 },
  'HUF': { symbol: 'Ft', name: 'Hungarian Forint', decimals: 2 },
  'INR': { symbol: '₹', name: 'Indian Rupee', decimals: 2 },
  'KRW': { symbol: '₩', name: 'South Korean Won', decimals: 0 },
  'MYR': { symbol: 'RM', name: 'Malaysian Ringgit', decimals: 2 },
  'THB': { symbol: '฿', name: 'Thai Baht', decimals: 2 },
  'BRL': { symbol: 'R$', name: 'Brazilian Real', decimals: 2 },
  'MXN': { symbol: '$', name: 'Mexican Peso', decimals: 2 },
  'ZAR': { symbol: 'R', name: 'South African Rand', decimals: 2 },
  'TRY': { symbol: '₺', name: 'Turkish Lira', decimals: 2 },
  'RUB': { symbol: '₽', name: 'Russian Ruble', decimals: 2 },
  'AED': { symbol: 'د.إ', name: 'UAE Dirham', decimals: 2 }
};

// Get currency symbol for display
export const getCurrencySymbol = (currency) => {
  return CURRENCY_INFO[currency]?.symbol || currency;
};

// Get currency name for display
export const getCurrencyName = (currency) => {
  return CURRENCY_INFO[currency]?.name || currency;
};

// Format amount with currency symbol
export const formatCurrencyAmount = (amount, currency, showSymbol = true) => {
  const info = CURRENCY_INFO[currency];
  if (!info) return `${currency} ${amount}`;
  
  const decimals = info.decimals;
  const formattedAmount = parseFloat(amount).toFixed(decimals);
  
  if (showSymbol) {
    return `${info.symbol}${formattedAmount}`;
  }
  return formattedAmount;
};

// Validate if currency is supported
export const isCurrencySupported = (currency) => {
  return SUPPORTED_CURRENCIES.includes(currency.toUpperCase());
};

// Get currencies most relevant for Bermuda users
export const getBermudaCurrencies = () => {
  return ['BMD', 'USD', 'EUR', 'GBP', 'CAD'];
};

// Get all supported currencies with their display info
export const getAllCurrenciesWithInfo = () => {
  return SUPPORTED_CURRENCIES.map(code => ({
    code,
    symbol: getCurrencySymbol(code),
    name: getCurrencyName(code),
    decimals: CURRENCY_INFO[code]?.decimals || 2
  }));
};

// Auto-detect currency based on user location/preferences
export const getDefaultCurrencyForCountry = (countryCode) => {
  const countryToCurrency = {
    'US': 'USD',
    'BM': 'BMD',  // Bermuda
    'CA': 'CAD',
    'GB': 'GBP',
    'AU': 'AUD',
    'JP': 'JPY',
    'CH': 'CHF',
    'CN': 'CNY',
    'HK': 'HKD',
    'NZ': 'NZD',
    'SG': 'SGD',
    // European countries
    'AT': 'EUR', 'BE': 'EUR', 'CY': 'EUR', 'EE': 'EUR', 'FI': 'EUR',
    'FR': 'EUR', 'DE': 'EUR', 'GR': 'EUR', 'IE': 'EUR', 'IT': 'EUR',
    'LV': 'EUR', 'LT': 'EUR', 'LU': 'EUR', 'MT': 'EUR', 'NL': 'EUR',
    'PT': 'EUR', 'SK': 'EUR', 'SI': 'EUR', 'ES': 'EUR',
    // Nordic countries
    'NO': 'NOK',
    'SE': 'SEK',
    'DK': 'DKK',
    // Other countries
    'PL': 'PLN',
    'CZ': 'CZK',
    'HU': 'HUF',
    'IN': 'INR',
    'KR': 'KRW',
    'MY': 'MYR',
    'TH': 'THB',
    'BR': 'BRL',
    'MX': 'MXN',
    'ZA': 'ZAR',
    'TR': 'TRY',
    'RU': 'RUB',
    'AE': 'AED'
  };
  
  return countryToCurrency[countryCode] || 'USD';
};

const currencyUtils = {
  SUPPORTED_CURRENCIES,
  CURRENCY_INFO,
  getCurrencySymbol,
  getCurrencyName,
  formatCurrencyAmount,
  isCurrencySupported,
  getBermudaCurrencies,
  getAllCurrenciesWithInfo,
  getDefaultCurrencyForCountry
};
export default currencyUtils; 